import { deleteDB, openDB as openIdb, type IDBPDatabase } from "idb";

export type DBConfig = {
  dbName: string;
  version: number;
  stores: ReadonlyArray<{ storeName: string; keyPath: string }>;
  storeName: string;
};

type IDBKey = IDBValidKey;

// One connection per database
const dbCache: Map<string, Promise<IDBPDatabase>> = new Map();

// Store schemas only need validating once per connection; see openDB.
const validatedStores: Set<string> = new Set();

const buildCacheKey = (config: DBConfig) => `${config.dbName}-${config.version || 1}`;

// Forgets every connection to a database and closes it once it is available
const dropConnections = (dbName: string) => {
  for (const [key, connection] of [...dbCache.entries()]) {
    if (!key.startsWith(`${dbName}-`)) continue;

    dbCache.delete(key);
    void connection.then(
      (db) => db.close(),
      () => {
        // The open failed, so there is no connection to close.
      }
    );
  }

  for (const key of validatedStores) {
    if (key.startsWith(`${dbName}-`)) {
      validatedStores.delete(key);
    }
  }
};

const deleteDatabaseInternal = async (dbName: string): Promise<void> => {
  dropConnections(dbName);
  await deleteDB(dbName, {
    blocked: () => {
      console.warn(`Delete blocked: please close all tabs using the database "${dbName}"`);
    },
  });
};

const connect = async (config: DBConfig, allowRecreate = true): Promise<IDBPDatabase> => {
  const { dbName, stores } = config;
  const version = config.version || 1;

  let rejectBlocked!: (error: Error) => void;
  const blockedRejection = new Promise<never>((_, reject) => {
    rejectBlocked = reject;
  });

  const opening = openIdb(dbName, version, {
    upgrade(db) {
      for (const spec of stores) {
        if (!db.objectStoreNames.contains(spec.storeName)) {
          db.createObjectStore(spec.storeName, { keyPath: spec.keyPath });
        }
      }
    },

    // An older tab still holding this database at a lower version blocks the
    // upgrade, and the open would otherwise stay pending forever. Failing
    // loudly gets the caller's error state instead of a dead page.
    blocked() {
      dropConnections(dbName);
      rejectBlocked(
        new Error(
          `Upgrade of "${dbName}" to version ${version} is blocked by another open tab. Close other tabs and reload.`
        )
      );
    },

    // Yield to a newer tab's upgrade rather than blocking it. Connections live
    // in dbCache for the lifetime of the tab, so without this every future
    // version bump strands anyone with a second tab open.
    blocking() {
      console.warn(`[IndexedDb] "${dbName}" is upgrading elsewhere; closing this connection.`);
      dropConnections(dbName);
    },

    terminated() {
      console.warn(`[IndexedDb] Connection to "${dbName}" closed unexpectedly; it will be reopened on next use.`);
      dropConnections(dbName);
    },
  });

  try {
    return await Promise.race([opening, blockedRejection]);
  } catch (error) {
    // The stored database is at a *higher* version than this build asks for, so
    // the open can never succeed.
    if (allowRecreate && error instanceof DOMException && error.name === "VersionError") {
      console.warn(
        `[IndexedDb] "${dbName}" is newer than version ${version}; recreating it. Cached data will be re-fetched.`
      );
      await deleteDatabaseInternal(dbName);
      return connect(config, false);
    }
    throw error;
  }
};

const openDB = async (config: DBConfig): Promise<IDBPDatabase> => {
  const { dbName, storeName } = config;
  const cacheKey = buildCacheKey(config);

  if (!dbCache.has(cacheKey)) {
    dbCache.set(cacheKey, connect(config));
  }

  const pending = dbCache.get(cacheKey)!;

  let db: IDBPDatabase;
  try {
    db = await pending;
  } catch (error) {
    // Otherwise a single failed open poisons the cache for the tab's lifetime.
    if (dbCache.get(cacheKey) === pending) {
      dbCache.delete(cacheKey);
    }
    throw error;
  }

  // If a DB/store was previously created with the wrong keying (e.g. autoIncrement numeric keys),
  // cached lookups by UUID will never hit. Detect and self-heal by recreating the DB.
  const keyPath = config.stores.find((s) => s.storeName === storeName)?.keyPath;
  const validationKey = `${cacheKey}-${storeName}`;

  if (keyPath && !validatedStores.has(validationKey) && db.objectStoreNames.contains(storeName)) {
    try {
      const existingKeyPath = db.transaction(storeName, "readonly").store.keyPath;

      if (existingKeyPath !== keyPath) {
        console.warn(
          `[IndexedDb] Store schema mismatch for "${dbName}/${storeName}": expected keyPath="${keyPath}", got "${String(
            existingKeyPath
          )}". Recreating DB to restore UUID-keyed caching.`
        );

        await deleteDatabaseInternal(dbName);
        console.log(`[IndexedDb] Deleted mismatched database: ${dbName}`);

        // Re-open now that DB has been cleared; upgrade handler will create correct store.
        return await openDB(config);
      }

      // The schema cannot change under a live connection, and this check costs a
      // transaction on every call otherwise.
      validatedStores.add(validationKey);
    } catch (e) {
      console.warn(`[IndexedDb] Failed validating store schema for "${dbName}/${storeName}":`, e);
    }
  }

  return db;
};

const getItem = async <T>(config: DBConfig, id: IDBKey): Promise<T | undefined> => {
  const db = await openDB(config);
  return (await db.get(config.storeName, id)) as T | undefined;
};

const getItemsByIds = async <T>(config: DBConfig, ids: IDBKey[]): Promise<T[]> => {
  if (ids.length === 0) return [];

  const db = await openDB(config);
  const tx = db.transaction(config.storeName, "readonly");

  // Every get is issued before the first await, so they all land in this
  // transaction rather than reopening one per id.
  const items = (await Promise.all(ids.map((id) => tx.store.get(id)))) as Array<T | undefined>;
  await tx.done;

  return items.filter((item): item is T => item !== undefined);
};

const getAllKeys = async (config: DBConfig): Promise<IDBKey[]> => {
  const db = await openDB(config);
  return db.getAllKeys(config.storeName);
};

/**
 * Read a store a page at a time, so only one page of (potentially large) records
 * is resident at once.
 */
const getAllItemsBatched = async <T>(
  config: DBConfig,
  processBatch: (items: T[]) => Promise<void>,
  batchSize = 100
): Promise<void> => {
  const db = await openDB(config);

  const { keyPath } = db.transaction(config.storeName, "readonly").store;
  if (typeof keyPath !== "string") {
    throw new Error(`[IndexedDb] "${config.storeName}" needs a single in-line keyPath to be read in pages.`);
  }

  let lastKey: IDBValidKey | undefined = undefined;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const range = lastKey !== undefined ? IDBKeyRange.lowerBound(lastKey, true) : undefined;
    const tx = db.transaction(config.storeName, "readonly");
    const batch = (await tx.store.getAll(range, batchSize)) as T[];
    await tx.done;

    if (batch.length === 0) return;

    lastKey = (batch[batch.length - 1] as Record<string, IDBValidKey | undefined>)[keyPath];
    if (lastKey === undefined) {
      throw new Error(`[IndexedDb] a record in "${config.storeName}" is missing its key ("${keyPath}").`);
    }

    await processBatch(batch);

    if (batch.length < batchSize) return;
  }
};

const upsertItems = async <T extends { id: IDBKey }>(config: DBConfig, items: T[]): Promise<void> => {
  if (items.length === 0) return;

  const db = await openDB(config);
  const tx = db.transaction(config.storeName, "readwrite");

  await Promise.all([...items.map((item) => tx.store.put(item)), tx.done]);
};

const deleteItem = async (config: DBConfig, id: IDBKey): Promise<void> => {
  const db = await openDB(config);
  await db.delete(config.storeName, id);
};

const clearStore = async (config: DBConfig): Promise<void> => {
  const db = await openDB(config);
  await db.clear(config.storeName);
  console.log(`All items in store "${config.storeName}" have been deleted.`);
};

const getCount = async (config: DBConfig): Promise<{ count: number }> => {
  const db = await openDB(config);
  return { count: await db.count(config.storeName) };
};

const closeDatabase = async (config: DBConfig): Promise<void> => {
  dropConnections(config.dbName);
  console.log(`Closed database: ${config.dbName}`);
};

const deleteDatabase = async (dbName: string): Promise<void> => {
  await deleteDatabaseInternal(dbName);
  console.log(`Deleted database: ${dbName}`);
};

export const IndexedDb = {
  getItem,
  getItemsByIds,
  getAllKeys,
  getAllItemsBatched,
  upsertItems,
  deleteItem,
  clearStore,
  getCount,
  closeDatabase,
  deleteDatabase,
};
